
import React, { useRef, useState } from 'react';
import './FacilityImages.css';

// TODO: Replace with real API call to fetch images for the station
const DEFAULT_IMAGES = [
  {
    src: 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=600&h=400&fit=crop',
    label: 'Dining Area',
  },
  {
    src: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=600&h=400&fit=crop',
    label: 'Nursing Room',
  },
  {
    src: 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=600&h=400&fit=crop',
    label: 'Play Area',
  },
  {
    src: 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=600&h=400&fit=crop',
    label: 'Changing Room',
  },
  {
    src: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=600&h=400&fit=crop',
    label: 'Seating Area',
  },
  {
    src: 'https://images.unsplash.com/photo-1559329007-40df8a9345d8?w=600&h=400&fit=crop',
    label: 'Outdoor Patio',
  },
];

const getAuthToken = () => {
  // TODO: Replace with your real auth token retrieval logic
  return localStorage.getItem('authToken') || '';
};

const getStationId = () => {
  // TODO: Replace with real station/location id logic (from props, context, or route)
  return 'demo-station-id';
};

const FacilityImages = () => {
  const [images, setImages] = useState(DEFAULT_IMAGES);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Step 1: Get pre-signed S3 upload URL from backend
  const getPresignedUrl = async (file: File) => {
    const token = getAuthToken();
    const stationId = getStationId();
    const contentType = file.type;
    const category = 'changing_station'; // or let user pick
    const url = `/photos/upload-url?stationId=${encodeURIComponent(stationId)}&contentType=${encodeURIComponent(contentType)}&category=${category}`;
    const res = await fetch(url, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to get upload URL');
    return res.json();
  };

  // Step 2: Upload file to S3
  const uploadToS3 = async (uploadUrl: string, file: File) => {
    const res = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file,
    });
    if (!res.ok) throw new Error('Failed to upload to S3');
  };

  // Step 3: Confirm upload with backend
  const confirmUpload = async ({ photoId, s3Key, stationId, caption, category }: {
    photoId: string;
    s3Key: string;
    stationId: string;
    caption: string;
    category: string;
  }) => {
    const token = getAuthToken();
    const res = await fetch('/photos/confirm', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ photoId, s3Key, stationId, caption, category }),
    });
    if (!res.ok) throw new Error('Failed to confirm upload');
    return res.json();
  };

  // Step 4: Handle file input change
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setError('');
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const file = files[0];
    setUploading(true);
    try {
      // 1. Get presigned URL
      const { uploadUrl, photoId, s3Key, finalUrl } = await getPresignedUrl(file);
      // 2. Upload to S3
      await uploadToS3(uploadUrl, file);
      // 3. Confirm upload
      const stationId = getStationId();
      const category = 'changing_station';
      const caption = '';
      const confirmRes = await confirmUpload({ photoId, s3Key, stationId, caption, category });
      // 4. Add new image to gallery
      setImages((imgs) => [
        { src: confirmRes.data?.imageUrl || finalUrl, label: category },
        ...imgs,
      ]);
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message || 'Upload failed');
      } else {
        setError('Upload failed');
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="facility-images">
      <h2 className="facility-images__title">Inside the space</h2>
      <button
        className="facility-images__upload-btn"
        onClick={() => fileInputRef.current && (fileInputRef.current as HTMLInputElement).click()}
        disabled={uploading}
      >
        {uploading ? 'Uploading...' : 'Upload Photo'}
      </button>
      <input
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        ref={fileInputRef}
        onChange={handleFileChange}
        data-testid="facility-upload-input"
      />
      {error && <div className="facility-images__error">{error}</div>}
      <div className="facility-images__gallery">
        {images.map((img, idx) => (
          <div key={idx} className="gallery-item">
            <img src={img.src} alt={img.label} className="gallery-item__image" />
            <span className="gallery-item__label">{img.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FacilityImages;
